import React, { useCallback, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { Button } from './Button';
import { ImageLightbox } from './ImageLightbox';
import { useCarousel } from '../hooks/useCarousel';
import { carouselImages } from '../data/carousel';
export function ProductCarousel() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const {
    scrollerRef,
    slideRefs,
    active,
    next,
    prev,
    select,
    onScroll,
    markInteracted,
    setHovering
  } = useCarousel(carouselImages.length, lightboxIndex !== null);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const navigateLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
    select(index);
  }, [select]);
  return <section className="py-20 bg-white border-y border-line" aria-labelledby="preview-title" data-id="element-265" data-mp="3htiz5i1tltw1">
      <div className="max-w-4xl mx-auto px-4 text-center mb-10" data-id="element-266" data-mp="3lucnudn37cpz">
        <h2 id="preview-title" className="text-3xl md:text-5xl font-display font-extrabold text-navy mb-4" data-id="element-267" data-mp="3lshymn4s8yg3">
          See Exactly What’s Inside Mountain Bike Coach Ready
        </h2>
        <p className="text-lg text-muted leading-relaxed" data-id="element-268" data-mp="32bnnf708ldvg">
          Real pages from the system. Visual coaching tools, technical progressions and
          ready-to-use MTB content built for real coaching sessions.
        </p>
      </div>

      <div className="relative" role="region" aria-roledescription="carousel" aria-label="Pages from Mountain Bike Coach Ready" onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)} data-id="element-269" data-mp="2nm9chuh0iody">
        <div ref={scrollerRef} onScroll={onScroll} onPointerDown={markInteracted} onTouchStart={markInteracted} className="relative flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory no-scrollbar px-4 md:px-[calc(50%-270px)] pb-2" data-id="element-270" data-mp="2eltdax7ay3ew">
          {carouselImages.map((image, index) => <figure key={image.src} ref={el => {
          slideRefs.current[index] = el;
        }} aria-roledescription="slide" aria-label={`${index + 1} of ${carouselImages.length}`} className={`snap-center shrink-0 w-full md:w-[540px] transition-opacity duration-200 ease-out ${active === index ? 'opacity-100' : 'md:opacity-60'}`} data-id="element-271" data-mp="22asbenw2xox2" data-mpi={index}>
              <button type="button" onClick={() => {
            markInteracted();
            setLightboxIndex(index);
          }} className="group relative block w-full rounded-2xl bg-sand p-2 sm:p-3 border border-line shadow-[0_18px_40px_rgba(27,42,71,0.14)] focus:outline-none focus-visible:ring-4 focus-visible:ring-seafoam" aria-label={`Enlarge: ${image.caption}`} data-id="element-272" data-mp="2l2csasmuo8un">
                <img src={image.src} alt={image.alt} loading={index === 0 ? 'eager' : 'lazy'} className="w-full h-auto rounded-xl" draggable={false} data-id="element-273" data-mp="14ntoh4v6vvcd" />
                <span className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/95 text-navy flex items-center justify-center shadow-md opacity-90 group-hover:opacity-100 transition-opacity duration-150" data-id="element-274" data-mp="3dycbxv26h81j">
                  <Maximize2 size={16} aria-hidden="true" data-id="element-275" data-mp="304upo2i3xh7t" />
                </span>
              </button>
            </figure>)}
        </div>

        <button type="button" onClick={prev} aria-label="Previous page" className="hidden sm:flex absolute left-4 md:left-[calc(50%-330px)] top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white text-navy border border-line shadow-lg items-center justify-center hover:bg-seafoam-light transition-colors duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-seafoam" data-id="element-276" data-mp="2v71j38cw6l70">
          <ChevronLeft size={24} data-id="element-277" data-mp="2cdfuctor0062" />
        </button>
        <button type="button" onClick={next} aria-label="Next page" className="hidden sm:flex absolute right-4 md:right-[calc(50%-330px)] top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white text-navy border border-line shadow-lg items-center justify-center hover:bg-seafoam-light transition-colors duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-seafoam" data-id="element-278" data-mp="2p5j5vt90aihh">
          <ChevronRight size={24} data-id="element-279" data-mp="38s79qvetw1rk" />
        </button>
      </div>

      <div className="flex items-center justify-center gap-2 mt-6" role="tablist" aria-label="Choose page" data-id="element-280" data-mp="3ct12q374o6rb">
        {carouselImages.map((image, index) => <button key={image.src} type="button" role="tab" aria-selected={active === index} aria-label={`Show page ${index + 1}: ${image.caption}`} onClick={() => select(index)} className="p-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-seafoam rounded-full" data-id="element-281" data-mp="3ms8c6faqrh95" data-mpi={index}>
            <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${active === index ? 'w-8 bg-orange' : 'w-2 bg-navy/20'}`} data-id="element-282" data-mp="1kw0z4lb1jk5q" />
          </button>)}
      </div>

      <div className="max-w-4xl mx-auto px-4 text-center mt-8" data-id="element-283" data-mp="16621cjneeaqs">
        <p className="text-sm sm:text-base font-semibold text-forest mb-6" data-id="element-284" data-mp="163pafgznwq1l">
          6 Complete Volumes · 150 Ready-to-Coach Sessions · Beginner to Advanced
        </p>
        <Button className="w-full sm:w-auto text-base md:text-lg py-4 px-8" data-id="element-285" data-mp="1yvsfutsxzenf">
          GET THE COMPLETE 6-IN-1 MTB COACHING SYSTEM
          <ArrowRight size={20} aria-hidden="true" className="shrink-0" data-id="element-286" data-mp="1txh6ek6k765q" />
        </Button>
      </div>

      <ImageLightbox images={carouselImages} index={lightboxIndex} onClose={closeLightbox} onNavigate={navigateLightbox} data-id="element-287" data-mp="1oynsiqnd1d51" />
    </section>;
}